import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiErrorMessage } from "@/api/client";
import { useParticipantAuth } from "@/context/ParticipantAuthContext";
import { DeadlinePill } from "@/components/DeadlinePill";
import "@/styles/activities_v3.scoped.css";
import {
  ArrowLeft, ChevronRight, Check, Edit3, Shield,
  Loader2, Send, CheckCircle2, Eye, FileDown, Lock, Paperclip,
} from "lucide-react";

const ATTACHMENT_ALLOWED_EXT = ["pdf", "png", "jpg", "jpeg"];
const ATTACHMENT_MAX_SIZE = 10 * 1024 * 1024; // 10 MB

function formatFileSize(bytes) {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const STEP_TITLES = ["Tu actividad", "Aplicación", "Aprendizaje"];
const BARRIERS = [
  "No se presentó una oportunidad apropiada",
  "Falta de tiempo o prioridades simultáneas",
  "Necesito aclarar cómo aplicar lo aprendido",
  "Dificultades de coordinación con otras personas",
  "Requiere apoyo o un procedimiento institucional",
  "Otra dificultad",
];
const QUESTION_LABELS = [
  { key: "situation", q: "1. ¿Qué situación abordaste?" },
  { key: "tool_used", q: "2. ¿Qué contenido, herramienta o idea del taller utilizaste?" },
  { key: "actions_taken", q: "3. ¿Qué hiciste concretamente?" },
  { key: "outcome", q: "4. ¿Qué resultado observaste?" },
  { key: "reflection", q: "5. ¿Qué aprendiste y qué harías diferente la próxima vez?" },
];

function uid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "req-" + Math.random().toString(36).slice(2) + Date.now();
}

export default function ActivityPage() {
  const { week } = useParams();
  const weekNum = parseInt(week, 10);
  const navigate = useNavigate();
  const { participant } = useParticipantAuth();

  const [weeksNav, setWeeksNav] = useState([]);
  const [activity, setActivity] = useState(null);
  const [screen, setScreen] = useState("form"); // form | review | success | viewing
  const [step, setStep] = useState(0);
  const [fields, setFields] = useState({ situation: "", tool_used: "", actions_taken: "", outcome: "", reflection: "" });
  const [barrier, setBarrier] = useState("");
  const [supportNeeded, setSupportNeeded] = useState("");
  const [mode, setMode] = useState("realizado");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [error, setError] = useState("");
  const [sendError, setSendError] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [attachment, setAttachment] = useState(null);
  const [attachUploading, setAttachUploading] = useState(false);
  const [attachRemoving, setAttachRemoving] = useState(false);
  const [attachError, setAttachError] = useState("");
  const fileInputRef = useRef(null);
  const requestIdRef = useRef(uid());
  const draftTimer = useRef(null);

  const loadNav = async () => {
    try {
      const { data } = await api.get("/participant/activities");
      setWeeksNav(data.weeks);
    } catch { /* noop */ }
  };

  const loadWeek = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/participant/activities/${weekNum}`);
      setActivity(data);
      setAttachment(data.attachment || null);
      setAttachError("");
      const sub = data.submission;
      if (sub && (sub.status === "realizado" || sub.status === "no_realizado")) {
        setFields({ situation: "", tool_used: "", actions_taken: "", outcome: "", reflection: "", ...sub.fields });
        setBarrier(sub.barrier || "");
        setSupportNeeded(sub.support_needed || "");
        setMode(sub.status);
        setScreen("success");
      } else if (sub) {
        setFields({ situation: "", tool_used: "", actions_taken: "", outcome: "", reflection: "", ...sub.fields });
        setScreen("form");
        setStep(0);
      } else {
        setFields({ situation: "", tool_used: "", actions_taken: "", outcome: "", reflection: "" });
        setScreen("form");
        setStep(0);
      }
    } catch (err) {
      setError(apiErrorMessage(err, "No fue posible cargar esta actividad."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadNav(); }, []);
  useEffect(() => { loadWeek(); requestIdRef.current = uid(); }, [weekNum]);

  const updateField = (key, value) => {
    const next = { ...fields, [key]: value };
    setFields(next);
    clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      api.put(`/participant/activities/${weekNum}/draft`, { fields: { [key]: value } }).catch(() => {});
    }, 500);
  };

  const goStep = (n) => { if (n >= 0 && n <= 2) setStep(n); };

  const goReview = () => { setScreen("review"); setConfirmChecked(false); setSendError(""); };
  const backToForm = () => { setScreen("form"); };

  const doSubmit = async () => {
    if (!confirmChecked) { setSendError("Confirma la declaración antes de registrar."); return; }
    setSaving(true);
    setSendError("");
    try {
      const payload = {
        fields, status: mode, confirm: true, request_id: requestIdRef.current,
        barrier: mode === "no_realizado" ? barrier : "",
        support_needed: mode === "no_realizado" ? supportNeeded : "",
      };
      const { data } = await api.post(`/participant/activities/${weekNum}/submit`, payload);
      setActivity((prev) => (prev ? { ...prev, submission: data.submission } : prev));
      setScreen("success");
      loadNav();
    } catch (err) {
      setSendError(apiErrorMessage(err, "No fue posible confirmar el envío. Tus respuestas permanecen en el formulario."));
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/participant/activities/${weekNum}/report.pdf`, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `Registro_Practica_ANID_Semana_${String(weekNum).padStart(2, "0")}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSendError(apiErrorMessage(err, "No fue posible descargar el comprobante en PDF."));
    } finally {
      setDownloading(false);
    }
  };

  const handleAttachmentSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setAttachError("");
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ATTACHMENT_ALLOWED_EXT.includes(ext)) {
      setAttachError("Formato no permitido. Solo se aceptan archivos PDF, PNG o JPEG.");
      return;
    }
    if (file.size > ATTACHMENT_MAX_SIZE) {
      setAttachError("El archivo supera el tamaño máximo permitido (10 MB).");
      return;
    }
    setAttachUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/participant/activities/${weekNum}/attachment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment(data.attachment);
    } catch (err) {
      setAttachError(apiErrorMessage(err, "No fue posible subir el archivo. Intenta nuevamente."));
    } finally {
      setAttachUploading(false);
    }
  };

  const handleAttachmentRemove = async () => {
    setAttachRemoving(true);
    setAttachError("");
    try {
      await api.delete(`/participant/activities/${weekNum}/attachment`);
      setAttachment(null);
    } catch (err) {
      setAttachError(apiErrorMessage(err, "No fue posible quitar el archivo."));
    } finally {
      setAttachRemoving(false);
    }
  };

  const downloadAttachment = async () => {
    try {
      const res = await api.get(`/participant/activities/${weekNum}/attachment/file`, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : (attachment?.original_filename || "adjunto");
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setAttachError(apiErrorMessage(err, "No fue posible descargar el adjunto."));
    }
  };

  const startBarrierFlow = () => { setMode("no_realizado"); setStep(2); };
  const cancelBarrierFlow = () => { setMode("realizado"); setStep(0); };
  const startEdit = () => { setScreen("form"); setStep(0); };
  if (loading) {
    return <div className="anid-v3-scope"><div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" style={{ color: "#4b68f6" }} /></div></div>;
  }

  if (error) {
    return (
      <div className="anid-v3-scope">
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="text-sm font-semibold" style={{ color: "#a34939" }}>{error}</p>
          <button onClick={() => navigate("/portal")} className="btn secondary">Volver al portal</button>
        </div>
      </div>
    );
  }

  const blocked = mode === "no_realizado" && step === 2 && screen === "form";
  const sub = activity?.submission;
  const isClosed = !!activity?.is_closed;
  const nextOpenWeek = weeksNav.find((w) => w.week > weekNum && w.published && w.is_open);

  return (
    <div className="anid-v3-scope">
      <header className="topbar">
        <div className="shell top-inner">
          <div className="brand">
            <svg className="brand-logo" viewBox="0 0 250 68" fill="none">
              <text x="4" y="44" fontFamily="Inter, sans-serif" fontWeight="900" fontSize="42" fill="#FFFFFF" letterSpacing="-1">Inside</text>
              <circle cx="138" cy="18" r="6" stroke="#B9C6E6" strokeWidth="1.2" fill="none" />
              <text x="138" y="21.5" fontFamily="Inter, sans-serif" fontWeight="700" fontSize="7.5" fill="#B9C6E6" textAnchor="middle">R</text>
              <text x="24" y="62" fontFamily="Inter, sans-serif" fontWeight="400" fontSize="14" fill="#A9B6D4" letterSpacing="0.4">Training &amp; Learning</text>
              <polygon points="174,4 212,14 180,30" fill="#F4CA54" />
              <polygon points="144,22 178,28 156,48" fill="#F47C48" />
              <polygon points="182,34 220,42 178,58" fill="#7C97F0" />
            </svg>
            <span className="brand-divider" />
            <div className="brand-caption">Curso de<b>Habilidades para el Liderazgo</b></div>
          </div>
          <div className="top-tools">
            <button className="ghost" type="button" onClick={() => navigate("/portal")} data-testid="activity-back-to-portal"><ArrowLeft className="icon" style={{ width: 17, height: 17 }} /> <span>Portal</span></button>
          </div>
        </div>
      </header>

      <section className="hero" aria-labelledby="heroTitle">
        <div className="shell hero-inner">
          <div>
            <div className="hero-eyebrow"><i /> BITÁCORA DE APLICACIÓN · ANID</div>
            <h1 id="heroTitle">Lo aprendido,<br /><span>en acción.</span></h1>
            <p>Registra la actividad de esta semana: qué hiciste, qué aplicaste y qué aprendiste.</p>
          </div>
        </div>
      </section>

      <main className="shell" id="app">
        <section className="journey" aria-label="Semanas del programa">
          <div className="journey-top">
            <span className="journey-label">TU RECORRIDO</span>
            <div className="journey-count">
              <strong>{weeksNav.filter((w) => w.submission?.status === "realizado").length} de 5 registros</strong>
              <span className="journey-line"><i style={{ width: `${weeksNav.filter((w) => w.submission?.status === "realizado").length * 20}%` }} /></span>
            </div>
          </div>
          <nav className="week-nav" aria-label="Seleccionar semana">
            {weeksNav.map((w) => {
              const done = w.submission?.status === "realizado" || w.submission?.status === "no_realizado";
              return (
                <button
                  key={w.week}
                  type="button"
                  data-testid={`activity-nav-week-${w.week}`}
                  className={`week-card ${w.week === weekNum ? "active" : ""} ${done ? "complete" : ""}`}
                  onClick={() => navigate(`/actividades/${w.week}`)}
                  disabled={!w.published || (!w.is_open && !done)}
                >
                  <span className="week-number">{done ? <Check className="icon" style={{ width: 14, height: 14 }} /> : String(w.week).padStart(2, "0")}</span>
                  <span className="week-text"><b>{w.topic}</b><small>{!w.published ? "No publicada" : done ? (w.submission.status === "realizado" ? "Registrado" : "Dificultad informada") : w.is_closed ? "Cerrada" : "Sin registro"}</small></span>
                </button>
              );
            })}
          </nav>
        </section>

        <div className="workspace" id="workspace">
          <aside className="side">
            <section className="week-detail">
              <div className="side-heading">
                <p className="eyebrow">SEMANA {String(weekNum).padStart(2, "0")} / 05</p>
                <h2>{activity.topic}</h2>
                {activity.due_date && (
                  <div style={{ marginTop: 12 }}>
                    <DeadlinePill dueDate={activity.due_date} published={true} />
                  </div>
                )}
                <div className="side-rule" />
              </div>
              <div className="assignment">
                <div className="assignment-title"><Edit3 className="icon" style={{ width: 15, height: 15 }} /> <span>Actividad de la semana</span></div>
                <p>{activity.title ? activity.title : "La tarea se define en cada sesión. Registra aquí la actividad acordada con tu relator o relatora."}</p>
                {activity.instructions && <p style={{ marginTop: 8 }}>{activity.instructions}</p>}
              </div>
            </section>
            <div className="side-privacy">
              <Shield className="icon" style={{ width: 18 }} />
              <p><b>Tu experiencia, con resguardo.</b>No incluyas nombres de otras personas, denuncias ni información sensible.</p>
            </div>
          </aside>

          <div>
            {screen === "form" && (
              <section className="workcard" aria-labelledby="cardTitle">
                <div className="card-top">
                  <div>
                    <h2 id="cardTitle">Mi registro de la semana</h2>
                    <small>Habilidades directivas · Registro individual</small>
                  </div>
                </div>

                {!blocked && (
                  <nav className="step-nav" aria-label="Pasos del registro">
                    {STEP_TITLES.map((t, i) => (
                      <button key={t} type="button" className={`step-btn ${step === i ? "active" : step > i ? "past" : ""}`} onClick={() => goStep(i)}>
                        <span className="step-dot">{step > i ? <Check className="icon" style={{ width: 12, height: 12 }} /> : i + 1}</span>{t}
                        {i < 2 && <ChevronRight className="icon step-arrow" />}
                      </button>
                    ))}
                  </nav>
                )}

                <div className="formbody">
                  {step === 0 && !blocked && (
                    <>
                      <div className="form-intro">
                        <p className="eyebrow">PASO 01 / 03</p>
                        <h3>¿Qué situación abordaste?</h3>
                        <p>Sin nombres de otras personas ni datos sensibles.</p>
                      </div>
                      <div className="field">
                        <label>¿Qué situación abordaste? <span className="req">*</span></label>
                        <textarea data-testid="activity-field-situation" rows={3} maxLength={1500} placeholder="Describe el contexto o la necesidad..." value={fields.situation} onChange={(e) => updateField("situation", e.target.value)} />
                      </div>
                      <div className="exception-link">
                        <button type="button" className="text-action" onClick={startBarrierFlow} data-testid="activity-report-barrier-btn">No pude realizar la actividad</button>
                        <small>Solo si necesitas informar una dificultad.</small>
                      </div>
                    </>
                  )}

                  {step === 1 && !blocked && (
                    <>
                      <div className="form-intro">
                        <p className="eyebrow">PASO 02 / 03</p>
                        <h3>¿Cómo la llevaste a la práctica?</h3>
                        <p>Una experiencia concreta es suficiente. Puedes responder con frases breves.</p>
                      </div>
                      <div className="field">
                        <label>¿Qué del taller utilizaste? <span className="req">*</span></label>
                        <textarea data-testid="activity-field-tool_used" rows={3} maxLength={1800} placeholder="Una idea, herramienta o forma de actuar..." value={fields.tool_used} onChange={(e) => updateField("tool_used", e.target.value)} />
                      </div>
                      <div className="field large">
                        <label>¿Qué hiciste? <span className="req">*</span></label>
                        <textarea data-testid="activity-field-actions_taken" rows={4} maxLength={4000} placeholder="Cuenta cómo aplicaste lo aprendido..." value={fields.actions_taken} onChange={(e) => updateField("actions_taken", e.target.value)} />
                      </div>
                    </>
                  )}

                  {step === 2 && !blocked && (
                    <>
                      <div className="form-intro">
                        <p className="eyebrow">PASO 03 / 03</p>
                        <h3>¿Qué te deja esta experiencia?</h3>
                        <p>No es necesario que el resultado sea positivo. Importa lo que observaste y aprendiste.</p>
                      </div>
                      <div className="field">
                        <label>¿Qué resultado observaste? <span className="req">*</span></label>
                        <textarea data-testid="activity-field-outcome" rows={3} maxLength={1800} placeholder="Después de aplicar la actividad..." value={fields.outcome} onChange={(e) => updateField("outcome", e.target.value)} />
                      </div>
                      <div className="field">
                        <label>¿Qué aprendiste y qué harías diferente? <span className="req">*</span></label>
                        <textarea data-testid="activity-field-reflection" rows={3} maxLength={1800} placeholder="Me di cuenta de que... / La próxima vez..." value={fields.reflection} onChange={(e) => updateField("reflection", e.target.value)} />
                      </div>
                      <AttachmentBox
                        attachment={attachment}
                        uploading={attachUploading}
                        removing={attachRemoving}
                        error={attachError}
                        onSelect={handleAttachmentSelect}
                        onRemove={handleAttachmentRemove}
                        inputRef={fileInputRef}
                      />
                      <div className="form-purpose">
                        <Shield className="icon" />
                        <p>Este registro permite dar seguimiento a tu participación. La aplicación de contenidos se revisa por separado; no es una evaluación de desempeño laboral.</p>
                      </div>
                    </>
                  )}

                  {blocked && (
                    <>
                      <div className="form-intro">
                        <p className="eyebrow">REGISTRO DE DIFICULTAD</p>
                        <h3>¿Qué te impidió realizarla?</h3>
                        <p>Este registro informa una dificultad. No cuenta como actividad realizada.</p>
                      </div>
                      <div className="field">
                        <label>Principal dificultad <span className="req">*</span></label>
                        <select data-testid="activity-barrier-select" value={barrier} onChange={(e) => setBarrier(e.target.value)}>
                          <option value="">Selecciona una opción</option>
                          {BARRIERS.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </div>
                      <div className="field">
                        <label>¿Qué apoyo u oportunidad necesitas? <span className="req">*</span></label>
                        <textarea data-testid="activity-support-needed" rows={3} maxLength={1800} placeholder="Para poder realizarla necesito..." value={supportNeeded} onChange={(e) => setSupportNeeded(e.target.value)} />
                      </div>
                      <div className="form-purpose">
                        <Shield className="icon" />
                        <p>Este registro permite dar seguimiento a tu participación. La aplicación de contenidos se revisa por separado; no es una evaluación de desempeño laboral.</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="formfoot">
                  <div className="footer-left">
                    {step > 0 || blocked ? (
                      <button className="btn link" type="button" onClick={() => (blocked ? cancelBarrierFlow() : goStep(step - 1))}><ArrowLeft className="icon" style={{ width: 15, height: 15 }} /> {blocked ? "Volver al registro" : "Anterior"}</button>
                    ) : (
                      <span className="save-foot">Borrador guardado automáticamente</span>
                    )}
                  </div>
                  <button className={`btn ${step === 2 || blocked ? "coral" : "primary"}`} type="button" onClick={() => (step === 2 || blocked ? goReview() : goStep(step + 1))} data-testid="activity-continue-btn">
                    {(step === 2 || blocked) ? "Revisar y enviar" : "Continuar"} <ChevronRight className="icon" style={{ width: 15, height: 15 }} />
                  </button>
                </div>
              </section>
            )}

            {screen === "review" && (
              <section className="workcard" aria-labelledby="reviewTitle" data-testid="activity-review-card">
                <div className="card-top">
                  <div>
                    <h2 id="reviewTitle">Revisar mi registro</h2>
                    <small>Semana {String(weekNum).padStart(2, "0")} · {activity.topic}</small>
                  </div>
                </div>
                <div className="formbody">
                  {mode !== "no_realizado" ? (
                    QUESTION_LABELS.map((item) => (
                      <div className="review-entry" key={item.key}>
                        <small>{item.q}</small>
                        <p>{fields[item.key] || "No informado"}</p>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="review-entry"><small>Tipo de registro</small><p>Dificultad informada. No es una actividad realizada.</p></div>
                      <div className="review-entry"><small>Dificultad</small><p>{barrier || "No informado"}</p></div>
                      <div className="review-entry"><small>Apoyo necesario</small><p>{supportNeeded || "No informado"}</p></div>
                    </>
                  )}
                  {mode !== "no_realizado" && (
                    <div className="review-entry">
                      <small>Evidencia adjunta</small>
                      <p data-testid="activity-review-attachment">{attachment ? attachment.original_filename : "Sin adjunto (opcional)"}</p>
                    </div>
                  )}
                  <p className="tip"><Shield className="icon" /> Este registro acredita participación y permite analizar la aplicación de los contenidos para mejorar el programa. No constituye una evaluación de desempeño laboral.</p>
                  <label className="confirm-check">
                    <input type="checkbox" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} data-testid="activity-confirm-checkbox" />
                    <span><b>{mode === "no_realizado" ? "Confirmo que estoy informando una dificultad, no una actividad realizada." : "Confirmo que realicé la actividad descrita."}</b> El registro representa mi experiencia y no incluye información sensible de otras personas.</span>
                  </label>
                  {sendError && <div className="errorbox" role="alert" style={{ marginTop: 15 }}>{sendError}</div>}
                </div>
                <div className="formfoot">
                  <button className="btn secondary" type="button" onClick={backToForm} data-testid="activity-edit-answers-btn"><Edit3 className="icon" style={{ width: 15, height: 15 }} /> Editar respuestas</button>
                  <button className="btn coral" type="button" disabled={saving} onClick={doSubmit} data-testid="activity-final-submit-btn">
                    {saving ? <Loader2 className="icon animate-spin" style={{ width: 16, height: 16 }} /> : <Send className="icon" style={{ width: 16, height: 16 }} />}
                    Confirmar y registrar
                  </button>
                </div>
              </section>
            )}

            {screen === "success" && (
              <SuccessCard activity={activity} sub={sub} mode={mode} weekNum={weekNum} isClosed={isClosed}
                nextOpenWeek={nextOpenWeek} navigate={navigate} onView={() => setScreen("viewing")}
                onEdit={startEdit} onDownload={downloadPdf} downloading={downloading} sendError={sendError}
                attachment={attachment} onDownloadAttachment={downloadAttachment} />
            )}

            {screen === "viewing" && (
              <ViewingCard activity={activity} sub={sub} mode={mode} weekNum={weekNum} isClosed={isClosed}
                fields={fields} barrier={barrier} supportNeeded={supportNeeded} participant={participant}
                onBack={() => setScreen("success")} onEdit={startEdit} onDownload={downloadPdf} downloading={downloading}
                attachment={attachment} onDownloadAttachment={downloadAttachment} />
            )}
          </div>
        </div>
      </main>

      <footer className="anid-footer">
        <div className="shell anid-footer-inner">
          <span>Herramienta de apoyo al programa desarrollada por INSIDE® Training &amp; Learning</span>
          <span className="anid-footer-muted">ANID · Programa de Formación y Desarrollo de Competencias de Liderazgo</span>
        </div>
      </footer>
    </div>
  );
}

function SuccessCard({ activity, sub, mode, weekNum, isClosed, nextOpenWeek, navigate, onView, onEdit, onDownload, downloading, sendError, attachment, onDownloadAttachment }) {
  return (
    <section className="workcard">
      <div className="success-box" data-testid="activity-success-box">
        <div className="success-symbol"><CheckCircle2 className="icon" style={{ width: 29, height: 29 }} /></div>
        <h3>{mode === "no_realizado" ? "Dificultad registrada correctamente" : "Actividad registrada correctamente"}</h3>
        <p>{mode === "no_realizado" ? "Registramos la dificultad informada para esta semana. No cuenta como actividad realizada." : "Tu registro de aplicación práctica de esta semana fue guardado correctamente."}</p>
        <div className="receipt">
          <div className="receipt-top"><span>SEMANA {String(weekNum).padStart(2, "0")}</span><span>Guardado en el servidor</span></div>
          <div className="receipt-body">
            <h4>{activity.title || activity.topic}</h4>
            <p data-testid="activity-submitted-at">Registrada el {sub?.submitted_at_display || "—"}</p>
            {mode === "realizado" && attachment && (
              <p data-testid="activity-success-attachment-filename" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Paperclip className="icon" style={{ width: 13, height: 13 }} /> {attachment.original_filename}
              </p>
            )}
          </div>
        </div>
        {sendError && <div className="errorbox" role="alert" style={{ marginTop: 4, marginBottom: 15, textAlign: "left" }}>{sendError}</div>}
        <div className="success-actions">
          <button className="btn secondary" type="button" onClick={onView} data-testid="activity-view-registro-btn"><Eye className="icon" style={{ width: 16, height: 16 }} /> Ver mi registro</button>
          {!isClosed ? (
            <button className="btn secondary" type="button" onClick={onEdit} data-testid="activity-edit-registro-btn"><Edit3 className="icon" style={{ width: 16, height: 16 }} /> Editar registro</button>
          ) : (
            <span className="closed-note"><Lock className="icon" style={{ width: 14, height: 14 }} /> Actividad cerrada</span>
          )}
          {mode === "realizado" && (
            <button className="btn secondary" type="button" disabled={downloading} onClick={onDownload} data-testid="activity-download-pdf-btn">
              {downloading ? <Loader2 className="icon animate-spin" style={{ width: 16, height: 16 }} /> : <FileDown className="icon" style={{ width: 16, height: 16 }} />} Descargar mi registro PDF
            </button>
          )}
          {mode === "realizado" && attachment && (
            <button className="btn secondary" type="button" onClick={onDownloadAttachment} data-testid="activity-download-attachment-btn">
              <Paperclip className="icon" style={{ width: 16, height: 16 }} /> Descargar adjunto
            </button>
          )}
          <button className="btn primary" type="button" onClick={() => navigate("/portal")} data-testid="activity-back-to-portal-btn">Volver al portal</button>
          {nextOpenWeek && (
            <button className="btn primary" type="button" onClick={() => navigate(`/actividades/${nextOpenWeek.week}`)} data-testid="activity-next-week-btn">
              Ir a la siguiente semana <ChevronRight className="icon" style={{ width: 15, height: 15 }} />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function ViewingCard({ activity, sub, mode, weekNum, isClosed, fields, barrier, supportNeeded, participant, onBack, onEdit, onDownload, downloading, attachment, onDownloadAttachment }) {
  return (
    <section className="workcard" data-testid="activity-viewing-card">
      <div className="card-top">
        <div>
          <h2>Ver mi registro</h2>
          <small>Semana {String(weekNum).padStart(2, "0")} · {activity.title || activity.topic}</small>
        </div>
      </div>
      <div className="formbody">
        <div className="review-person">
          <div className="avatar">{(participant?.full_name || "P").slice(0, 1).toUpperCase()}</div>
          <div>
            <h3>{participant?.full_name || "Participante"}</h3>
            <p>
              Enviado: {sub?.submitted_at_display || "—"}
              {sub?.updated_at_display && sub.updated_at_display !== sub.submitted_at_display ? ` · Última modificación: ${sub.updated_at_display}` : ""}
            </p>
          </div>
        </div>

        {mode !== "no_realizado" ? (
          QUESTION_LABELS.map((item) => (
            <div className="review-entry" key={item.key}>
              <small>{item.q}</small>
              <p>{fields[item.key] || "No informado"}</p>
            </div>
          ))
        ) : (
          <>
            <div className="review-entry"><small>Tipo de registro</small><p>Dificultad informada. No es una actividad realizada.</p></div>
            <div className="review-entry"><small>Dificultad</small><p>{barrier || "No informado"}</p></div>
            <div className="review-entry"><small>Apoyo necesario</small><p>{supportNeeded || "No informado"}</p></div>
          </>
        )}
        {mode !== "no_realizado" && (
          <div className="review-entry">
            <small>Evidencia adjunta</small>
            {attachment ? (
              <button type="button" className="text-action" onClick={onDownloadAttachment} data-testid="activity-viewing-attachment-download-btn" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Paperclip className="icon" style={{ width: 13, height: 13 }} /> {attachment.original_filename} · Descargar
              </button>
            ) : (
              <p className="help">Sin adjunto (opcional).</p>
            )}
          </div>
        )}
      </div>
      <div className="formfoot">
        <button className="btn link" type="button" onClick={onBack}><ArrowLeft className="icon" style={{ width: 15, height: 15 }} /> Volver</button>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {!isClosed && (
            <button className="btn secondary" type="button" onClick={onEdit} data-testid="activity-viewing-edit-btn"><Edit3 className="icon" style={{ width: 15, height: 15 }} /> Editar registro</button>
          )}
          {mode === "realizado" && (
            <button className="btn primary" type="button" disabled={downloading} onClick={onDownload} data-testid="activity-viewing-download-btn">
              {downloading ? <Loader2 className="icon animate-spin" style={{ width: 16, height: 16 }} /> : <FileDown className="icon" style={{ width: 16, height: 16 }} />} Descargar mi registro PDF
            </button>
          )}
        </div>
      </div>
    </section>
  );
}


function AttachmentBox({ attachment, uploading, removing, error, onSelect, onRemove, inputRef }) {
  return (
    <div className="field" data-testid="activity-attachment-field">
      <label>Adjunta evidencia <span className="optional">opcional · PDF, PNG o JPEG · máx. 10MB</span></label>
      {attachment ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", border: "1px solid #dce3ee", borderRadius: 10, padding: "11px 14px", background: "#fbfcfe" }}>
          <Paperclip className="icon" style={{ width: 16, height: 16, color: "#647aa6", flexShrink: 0 }} />
          <span data-testid="activity-attachment-filename" style={{ fontSize: 12, color: "#30425f", fontWeight: 600, overflowWrap: "anywhere" }}>{attachment.original_filename}</span>
          {!!formatFileSize(attachment.size) && <span style={{ fontSize: 10, color: "#96a0b2" }}>{formatFileSize(attachment.size)}</span>}
          <span style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
            <button type="button" className="text-action" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="activity-attachment-replace-btn">
              {uploading ? "Subiendo..." : "Reemplazar"}
            </button>
            <button type="button" className="text-action" onClick={onRemove} disabled={removing || uploading} data-testid="activity-attachment-remove-btn" style={{ color: "#a34939" }}>
              {removing ? "Quitando..." : "Quitar"}
            </button>
          </span>
        </div>
      ) : (
        <button type="button" className="btn secondary" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="activity-attachment-upload-btn">
          {uploading ? <Loader2 className="icon animate-spin" style={{ width: 15, height: 15 }} /> : <Paperclip className="icon" style={{ width: 15, height: 15 }} />}
          {uploading ? "Subiendo..." : "Adjuntar archivo"}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
        hidden
        onChange={onSelect}
        data-testid="activity-attachment-input"
      />
      {error && <p className="help" style={{ color: "#a34939", marginTop: 8 }} data-testid="activity-attachment-error">{error}</p>}
    </div>
  );
}
